import type { Node, Edge } from "@xyflow/react";
import type { PacketGraphDetail } from "../../types/shadow-run";

export interface NodeColorScheme {
  background: string;
  border: string;
  text: string;
}

const NODE_COLOR_MAP: Record<string, NodeColorScheme> = {
  taxaggregator: { background: "#ede9fe", border: "#8b5cf6", text: "#5b21b6" },
  deductioncomponent: { background: "#fef3c7", border: "#f59e0b", text: "#92400e" },
  additioncomponent: { background: "#dbeafe", border: "#3b82f6", text: "#1e40af" },
  hoursaggregator: { background: "#ccfbf1", border: "#14b8a6", text: "#115e59" },
  superaggregator: { background: "#e0e7ff", border: "#6366f1", text: "#3730a3" },
  globalparameter: { background: "#f3f4f6", border: "#9ca3af", text: "#374151" },
  componentparameter: { background: "#f3f4f6", border: "#9ca3af", text: "#374151" },
  // Derived categories for "Unknown" nodes
  summary: { background: "#d1fae5", border: "#10b981", text: "#065f46" },
  tax: { background: "#ede9fe", border: "#8b5cf6", text: "#5b21b6" },
  deduction: { background: "#fef3c7", border: "#f59e0b", text: "#92400e" },
  computed: { background: "#e0f2fe", border: "#0ea5e9", text: "#0c4a6e" },
};

const DEFAULT_COLOR: NodeColorScheme = {
  background: "#f9fafb",
  border: "#d1d5db",
  text: "#374151",
};

const SUMMARY_PATTERN = /NetPay|NetEarnings|GrossEarnings|GrossTax/i;
const NZ_PATTERN = /^Nz/i;
const DEDUCTION_PATTERN = /Deductions/i;

/**
 * Derive a meaningful display category when the backend reports "Unknown".
 * Returns the original nodeClass when it is a known taxonomy value.
 */
export function resolveDisplayCategory(
  nodeClass: string,
  nodeType: string,
  label: string,
): string {
  if (nodeClass.toLowerCase() !== "unknown") return nodeClass;

  if (SUMMARY_PATTERN.test(label)) return "Summary";
  if (NZ_PATTERN.test(nodeType) || NZ_PATTERN.test(label)) return "Tax";
  if (DEDUCTION_PATTERN.test(label)) return "Deduction";
  return "Computed";
}

export function getNodeColorScheme(
  nodeClass: string,
  nodeType: string,
  label: string = "",
): NodeColorScheme {
  const category = resolveDisplayCategory(nodeClass, nodeType, label);
  return NODE_COLOR_MAP[category.toLowerCase()] ?? DEFAULT_COLOR;
}

export interface CalculationNodeData extends Record<string, unknown> {
  label: string;
  nodeClass: string;
  displayCategory: string;
  nodeType: string;
  calculatedAmount: number;
  calculationDescription: string;
  hasVariance: boolean;
  varianceAmount: number | null;
  hasValue: boolean;
  isSelected: boolean;
  isHighlighted: boolean;
  isOnVariancePath: boolean;
}

export function toReactFlowGraph(graph: PacketGraphDetail): {
  nodes: Node<CalculationNodeData>[];
  edges: Edge[];
} {
  const nodes: Node<CalculationNodeData>[] = graph.nodes.map((n) => ({
    id: n.node_id,
    type: "calculationNode",
    position: { x: 0, y: 0 },
    data: {
      label: n.label,
      nodeClass: n.node_class,
      displayCategory: resolveDisplayCategory(n.node_class, n.node_type, n.label),
      nodeType: n.node_type,
      calculatedAmount: n.calculated_amount,
      calculationDescription: n.calculation_description,
      hasVariance: n.has_variance,
      varianceAmount: n.variance_amount,
      hasValue: n.has_value,
      isSelected: false,
      isHighlighted: false,
      isOnVariancePath: false,
    },
  }));

  const edges: Edge[] = graph.edges.map((e) => ({
    id: `${e.source_node_id}->${e.target_node_id}`,
    source: e.source_node_id,
    target: e.target_node_id,
    type: "calculationEdge",
    data: {
      isOnVariancePath: false,
      isHighlighted: false,
    },
  }));

  return { nodes, edges };
}

export function findVariancePaths(
  nodes: Node<CalculationNodeData>[],
  edges: Edge[],
  rootNodeId: string | null,
): { varianceNodeIds: Set<string>; varianceEdgeIds: Set<string> } {
  const varianceNodeIds = new Set<string>();
  const varianceEdgeIds = new Set<string>();

  const varianceStarters = nodes.filter((n) => n.data.hasVariance);
  if (varianceStarters.length === 0) {
    return { varianceNodeIds, varianceEdgeIds };
  }

  // Backend convention: edge.source depends on edge.target (source → target = consumer → dependency).
  // To trace from a variance node toward root, we follow target → sources
  // (find nodes that depend on the current node, i.e. its consumers).
  const towardRoot = new Map<string, { consumerId: string; edgeId: string }[]>();
  for (const edge of edges) {
    const list = towardRoot.get(edge.target) ?? [];
    list.push({ consumerId: edge.source, edgeId: edge.id });
    towardRoot.set(edge.target, list);
  }

  // BFS from each variance node toward root
  for (const vNode of varianceStarters) {
    const queue = [vNode.id];
    const visited = new Set<string>();
    while (queue.length > 0) {
      const current = queue.shift()!;
      if (visited.has(current)) continue;
      visited.add(current);
      varianceNodeIds.add(current);

      const consumers = towardRoot.get(current) ?? [];
      for (const { consumerId, edgeId } of consumers) {
        varianceEdgeIds.add(edgeId);
        if (!visited.has(consumerId)) {
          queue.push(consumerId);
        }
      }
    }
  }

  // Also ensure root is included if any variance exists
  if (varianceStarters.length > 0 && rootNodeId !== null) {
    varianceNodeIds.add(rootNodeId);
  }

  return { varianceNodeIds, varianceEdgeIds };
}

export function traceNodePath(
  selectedNodeId: string,
  edges: Edge[],
): { nodeIds: Set<string>; edgeIds: Set<string> } {
  const nodeIds = new Set<string>();
  const edgeIds = new Set<string>();

  // Build both directions
  const sourceToTargets = new Map<string, { targetId: string; edgeId: string }[]>();
  const targetToSources = new Map<string, { sourceId: string; edgeId: string }[]>();

  for (const edge of edges) {
    const fwd = sourceToTargets.get(edge.source) ?? [];
    fwd.push({ targetId: edge.target, edgeId: edge.id });
    sourceToTargets.set(edge.source, fwd);

    const bwd = targetToSources.get(edge.target) ?? [];
    bwd.push({ sourceId: edge.source, edgeId: edge.id });
    targetToSources.set(edge.target, bwd);
  }

  // BFS upstream (toward dependencies / sources)
  const upQueue = [selectedNodeId];
  const upVisited = new Set<string>();
  while (upQueue.length > 0) {
    const current = upQueue.shift()!;
    if (upVisited.has(current)) continue;
    upVisited.add(current);
    nodeIds.add(current);

    for (const { sourceId, edgeId } of targetToSources.get(current) ?? []) {
      edgeIds.add(edgeId);
      if (!upVisited.has(sourceId)) {
        upQueue.push(sourceId);
      }
    }
  }

  // BFS downstream (toward dependents / targets)
  const downQueue = [selectedNodeId];
  const downVisited = new Set<string>();
  while (downQueue.length > 0) {
    const current = downQueue.shift()!;
    if (downVisited.has(current)) continue;
    downVisited.add(current);
    nodeIds.add(current);

    for (const { targetId, edgeId } of sourceToTargets.get(current) ?? []) {
      edgeIds.add(edgeId);
      if (!downVisited.has(targetId)) {
        downQueue.push(targetId);
      }
    }
  }

  return { nodeIds, edgeIds };
}

export function filterByDepth(
  nodes: Node<CalculationNodeData>[],
  edges: Edge[],
  rootNodeId: string | null,
  maxDepth: number,
  rootNodeIds: string[] = [],
): { nodes: Node<CalculationNodeData>[]; edges: Edge[] } {
  // Determine starting roots for BFS
  const startIds = rootNodeId !== null ? [rootNodeId] : rootNodeIds;
  if (startIds.length === 0) {
    // No roots at all — return everything unfiltered
    return { nodes, edges };
  }

  // Backend convention: edge.source depends on edge.target (source → target = consumer → dependency).
  // BFS from root(s) follows source → targets to traverse down toward leaves.
  const sourceToTargets = new Map<string, string[]>();
  for (const edge of edges) {
    const list = sourceToTargets.get(edge.source) ?? [];
    list.push(edge.target);
    sourceToTargets.set(edge.source, list);
  }

  const included = new Set<string>();
  const queue: { id: string; depth: number }[] = startIds.map((id) => ({
    id,
    depth: 0,
  }));
  while (queue.length > 0) {
    const { id, depth } = queue.shift()!;
    if (included.has(id)) continue;
    if (depth > maxDepth) continue;
    included.add(id);

    for (const targetId of sourceToTargets.get(id) ?? []) {
      if (!included.has(targetId)) {
        queue.push({ id: targetId, depth: depth + 1 });
      }
    }
  }

  return {
    nodes: nodes.filter((n) => included.has(n.id)),
    edges: edges.filter(
      (e) => included.has(e.source) && included.has(e.target),
    ),
  };
}

export function filterToVariancePaths(
  nodes: Node<CalculationNodeData>[],
  edges: Edge[],
  varianceNodeIds: Set<string>,
  varianceEdgeIds: Set<string>,
): { nodes: Node<CalculationNodeData>[]; edges: Edge[] } {
  return {
    nodes: nodes.filter((n) => varianceNodeIds.has(n.id)),
    edges: edges.filter((e) => varianceEdgeIds.has(e.id)),
  };
}

/**
 * Topological sort of the traced subgraph in computation order
 * (leaf inputs first → root/aggregate last).
 *
 * Edge convention: source depends on target.
 * Kahn's algorithm: nodes with no dependencies (not a source of any edge) are
 * processed first; when a node is processed we decrement the dependency count
 * of every node that depends on it.
 */
export function computeImpactTrace(
  tracedNodeIds: Set<string>,
  tracedEdgeIds: Set<string>,
  allNodes: Node<CalculationNodeData>[],
  allEdges: Edge[],
): Node<CalculationNodeData>[] {
  const subNodes = allNodes.filter((n) => tracedNodeIds.has(n.id));
  const subEdges = allEdges.filter((e) => tracedEdgeIds.has(e.id));

  // depCount[node] = how many dependencies it still needs (edges where it is source)
  const depCount = new Map<string, number>();
  // dependents[target] = list of source nodes that depend on target
  const dependents = new Map<string, string[]>();

  for (const node of subNodes) {
    depCount.set(node.id, 0);
    dependents.set(node.id, []);
  }

  for (const edge of subEdges) {
    depCount.set(edge.source, (depCount.get(edge.source) ?? 0) + 1);
    const list = dependents.get(edge.target) ?? [];
    list.push(edge.source);
    dependents.set(edge.target, list);
  }

  const queue: string[] = [];
  for (const [id, count] of depCount) {
    if (count === 0) queue.push(id);
  }

  const ordered: string[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    ordered.push(id);

    for (const dependentId of dependents.get(id) ?? []) {
      const newCount = (depCount.get(dependentId) ?? 1) - 1;
      depCount.set(dependentId, newCount);
      if (newCount === 0) {
        queue.push(dependentId);
      }
    }
  }

  const nodeMap = new Map<string, Node<CalculationNodeData>>();
  for (const node of subNodes) {
    nodeMap.set(node.id, node);
  }

  return ordered
    .map((id) => nodeMap.get(id))
    .filter((n): n is Node<CalculationNodeData> => n !== undefined);
}

export function computeMaxDepth(
  nodes: Node<CalculationNodeData>[],
  edges: Edge[],
  rootNodeId: string | null,
  rootNodeIds: string[] = [],
): number {
  if (nodes.length === 0) return 0;

  const startIds = rootNodeId !== null ? [rootNodeId] : rootNodeIds;
  if (startIds.length === 0) return 0;

  // Backend convention: edge.source depends on edge.target (source → target = consumer → dependency).
  // BFS from root(s) follows source → targets to traverse down toward leaves.
  const sourceToTargets = new Map<string, string[]>();
  for (const edge of edges) {
    const list = sourceToTargets.get(edge.source) ?? [];
    list.push(edge.target);
    sourceToTargets.set(edge.source, list);
  }

  let max = 0;
  const queue: { id: string; depth: number }[] = startIds.map((id) => ({
    id,
    depth: 0,
  }));
  const visited = new Set<string>();
  while (queue.length > 0) {
    const { id, depth } = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);
    if (depth > max) max = depth;

    for (const targetId of sourceToTargets.get(id) ?? []) {
      if (!visited.has(targetId)) {
        queue.push({ id: targetId, depth: depth + 1 });
      }
    }
  }

  return max;
}
